// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState } from 'react';

import type { SurveyType } from '../../api/types';
import { ENV_LABEL, ENV_TONE } from '../../kortlaegning/vocab';
import { coveredTypeIds, linkableTypes, partCoveredByType, type SampleLinks } from '../../miljoe/model';
import { Checkbox } from '../Checkbox';
import { Pill } from '../Pill';
import styles from './LinkPicker.module.css';

export interface LinkPickerProps {
  types: SurveyType[];
  /** The links as the user last set them. */
  selected: SampleLinks;
  onToggle: (typeId: number) => void;
  /** Toggles a single part link. */
  onTogglePart: (code: string) => void;
  legend?: string;
}

/**
 * The survey types a sample can cover, each with its current miljøstatus, as
 * checkboxes — and under each type its parts, so a sample can cover one
 * specific bygningsdel instead of the whole type (frontend-r46). Each toggle
 * is a field commit — the caller sends it at once. Rejected types are hidden
 * unless already linked; then they show "Afvist" so they can be unlinked.
 */
export function LinkPicker({ types, selected, onToggle, onTogglePart, legend = 'Koblet til typer og dele' }: LinkPickerProps) {
  // A rejected type stays offered while it, or one of its parts, is linked — so the link can be undone.
  const options = linkableTypes(types, coveredTypeIds(selected, types));
  return (
    <fieldset className={styles.picker}>
      <legend className={styles.legend}>{legend}</legend>
      {options.length === 0 ? (
        <p className={styles.empty}>Ingen typer i kortlægningen endnu — prøven kan kobles senere.</p>
      ) : (
        <ul className={styles.list}>
          {options.map((t) => (
            <TypeOption key={t.id} type={t} selected={selected} onToggle={onToggle} onTogglePart={onTogglePart} />
          ))}
        </ul>
      )}
    </fieldset>
  );
}

function TypeOption({
  type: t,
  selected,
  onToggle,
  onTogglePart,
}: {
  type: SurveyType;
  selected: SampleLinks;
  onToggle: (typeId: number) => void;
  onTogglePart: (code: string) => void;
}) {
  const typeLinked = selected.type_ids.includes(t.id);
  const linkedParts = t.parts.filter((p) => selected.part_codes.includes(p.code)).length;
  // A type with a linked part opens on its parts, so the link is never hidden.
  const [open, setOpen] = useState(linkedParts > 0);
  const partsId = `link-parts-${t.id}`;

  return (
    <li>
      <div className={styles.row}>
        <label className={styles.option}>
          <Checkbox checked={typeLinked} onChange={() => onToggle(t.id)} />
          <span className={styles.name}>{t.name}</span>
          {t.review_status === 'rejected' ? (
            <Pill tone="wait">Afvist</Pill>
          ) : (
            <Pill tone={ENV_TONE[t.environment_status]}>{ENV_LABEL[t.environment_status]}</Pill>
          )}
        </label>
        {t.parts.length > 0 && (
          <button
            type="button"
            className={styles.partsToggle}
            aria-expanded={open}
            aria-controls={open ? partsId : undefined}
            aria-label={`${open ? 'Skjul' : 'Vis'} dele under ${t.name}`}
            onClick={() => setOpen((o) => !o)}
          >
            {linkedParts > 0 ? `${linkedParts}/${t.parts.length}` : t.parts.length} {t.parts.length === 1 ? 'del' : 'dele'}{' '}
            <span aria-hidden="true">{open ? '▾' : '▸'}</span>
          </button>
        )}
      </div>
      {open && (
        <ul id={partsId} className={styles.parts} aria-label={`Dele under ${t.name}`}>
          {t.parts.map((p) => {
            const covered = typeLinked && partCoveredByType(p, selected.type_ids);
            return (
              <li key={p.code}>
                <label className={covered ? `${styles.option} ${styles.covered}` : styles.option}>
                  <Checkbox
                    checked={covered || selected.part_codes.includes(p.code)}
                    disabled={covered}
                    onChange={() => onTogglePart(p.code)}
                  />
                  <span className={`${styles.code} mono`}>{p.code}</span>
                  <span className={styles.name}>{p.room_name || 'Uden rum'}</span>
                  {covered && <span className={styles.coveredNote}>dækket af typen</span>}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
